import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';
// Modal ↔ ConfirmModal은 의도된 상호 참조다(ConfirmModal은 base 위 규약, base는 dirty 확인에
// ConfirmModal을 쓴다). 둘 다 `export function` 선언 + 참조가 렌더 시점뿐이라 ESM 순환에 안전하다.
import { ConfirmModal } from './ConfirmModal';

/** 포커스 가능한 요소 셀렉터. disabled·tabindex=-1 제외. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * 닫기 ✕ — 목업 `.modal-close svg`(18px). 텍스트 글리프였던 것을 SVG로 바꿨다.
 *
 * `apps/web`의 아이콘 세트를 쓰지 않는 이유: `packages/ui`는 앱에 의존하지 않는다
 * (그 방향 의존이 생기면 admin이 web 아이콘을 끌어오게 된다). 이 한 개만 여기서 소유한다.
 *
 * ⚠️ **치수를 임의값(`h-[18px]`)으로 쓰지 않는다.** `packages/ui`는 앱 루트 밖이라 Tailwind JIT가
 * dev watch에서 변경을 못 집고, 처음 쓰는 임의값은 **클래스만 붙고 조용히 무스타일**이 된다
 * (2026-07-28 실측: `h-[34px]`·`h-[18px]` 둘 다 web·admin CSS에 미생성). 표준 유틸만 쓸 것.
 */
function CloseGlyph() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

/**
 * dirty 확인 모달의 층 — **부모 모달 바로 위**여야 한다(z 정책: 70→80→90→100, 층은 여는 쪽이
 * 주입). 부모의 overlayZClassName에서 한 층 올린다. 목록 밖 커스텀 값이면 최상위로 폴백.
 */
const DIRTY_CONFIRM_Z_ABOVE: Record<string, string> = {
  'z-[70]': 'z-[80]',
  'z-[80]': 'z-[90]',
  'z-[90]': 'z-[100]',
  'z-[100]': 'z-[100]',
};

/**
 * 겹쳐 뜨는 확인 오버레이 농도 — 아래 모달이 비쳐야 맥락이 유지되고, 알파가 곱해져
 * (0.4×2 = 0.64) 과하게 어두워지는 것도 피한다(web `SUB_MODAL_OVERLAY_BG`와 같은 규약 —
 * packages/ui는 앱에 의존하지 않으므로 값을 여기서 소유한다).
 */
const DIRTY_CONFIRM_OVERLAY_BG = 'bg-black/25';

const ModalRequestCloseContext = createContext<(() => void) | null>(null);

/**
 * base [[Modal]] 하위에서 "닫기 요청"을 얻는다 — footer의 명시 취소 버튼용.
 *
 * Esc·오버레이·✕는 base가 이미 dirty 확인([[ModalProps.isDirty]])을 거치지만, footer의
 * 취소 버튼은 각 모달이 소유하므로 base가 잡을 수 없다. 취소 버튼이 `onClose`를 직접 부르면
 * dirty 확인을 우회하니, **모달 내부의 닫기 성격 버튼은 이 함수를 쓰는 게 규약**이다
 * (dirty가 아니면 그대로 onClose라 동작 차이 없음). Modal 밖에서 호출되면 null —
 * 호출부는 `requestClose ?? fallbackClose` 형태로 쓴다.
 *
 * ⚠️ context는 Modal의 children **하위 컴포넌트**에서만 잡힌다 — 모달을 여는 부모 컴포넌트
 * 본문에서 훅을 불러 JSX에 끼워 넣으면 null이다(footer를 작은 컴포넌트로 분리할 것).
 */
export function useModalRequestClose(): (() => void) | null {
  return useContext(ModalRequestCloseContext);
}

const SIZE_CLASS = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
} as const;

export type ModalSize = keyof typeof SIZE_CLASS;

/**
 * 본문 스크롤([[ModalProps.bodyScroll]])의 세 조각 — 패널 / 고정 블록(헤더·푸터) / 본문.
 *
 * ⚠️ **임의값(`max-h-[90vh]`)을 쓰지 않는다 — 표준 유틸만이다.** `packages/ui`는 앱 루트 밖이라
 * 처음 쓰는 값이 조용히 무스타일이 될 수 있다(위 `CloseGlyph` 주석의 2026-07-28 실측). 여기 쓰는
 * 여섯 유틸은 **web·admin 앱 소스 양쪽에 이미 쓰이고 있어** packages/ui 스캔 여부와 무관하게
 * CSS에 존재한다(2026-10-26 census: `max-h-full` web 3·admin 1 / `min-h-0` 6·8 /
 * `overflow-y-auto` 61·44 / `flex-col` 72·29 / `flex-none` 26·1 / `overflow-hidden` 62·40).
 *
 * 높이 상한을 `vh` 손값이 아니라 `max-h-full`로 잡는 이유: 오버레이가 `fixed inset-0 … p-4`라
 * 그 content box가 곧 「뷰포트 − 여백」이고, 패널은 그 flex 자식이므로 `max-height: 100%`가
 * **여백을 이미 반영한 값**이 된다(`90vh`는 여백을 두 번 세거나 모자란다).
 *
 * `min-h-0`이 본문에 **필수**다 — flex 자식의 기본 `min-height: auto`는 콘텐츠 아래로 줄어드는
 * 것을 막아, 그것 없이는 `overflow-y-auto`가 붙어도 스크롤이 생기지 않는다.
 *
 * 패널 `overflow-hidden`은 스크롤 영역이 `rounded-modal` 모서리를 넘어 그려지는 것을 막는다
 * (푸터가 없는 모달에서 본문 끝이 둥근 모서리 위로 각지게 올라온다).
 */
const BODY_SCROLL_PANEL_CLASS = 'flex max-h-full flex-col overflow-hidden';
const BODY_SCROLL_FIXED_CLASS = 'flex-none';
const BODY_SCROLL_BODY_CLASS = 'min-h-0 flex-1 overflow-y-auto';

export interface ModalProps {
  /** 열림 여부. false면 렌더하지 않음(포탈도 미생성). */
  open: boolean;
  /** 닫기 요청(Esc·오버레이·✕). 상태는 소비자가 관리. */
  onClose: () => void;
  /** 헤더 제목. 문자열이면 aria-label로도 사용. 생략 시 헤더 제목 없음. */
  title?: ReactNode;
  /** 본문. */
  children: ReactNode;
  /** 하단 액션 영역(버튼 등). 생략 시 푸터 미표시. */
  footer?: ReactNode;
  /** 최대 폭. 기본 md. */
  size?: ModalSize;
  /** 오버레이 클릭으로 닫기. 기본 true. */
  closeOnOverlay?: boolean;
  /** Esc로 닫기. 기본 true. */
  closeOnEsc?: boolean;
  /** 헤더 ✕ 닫기 버튼 표시. 기본 true. */
  showCloseButton?: boolean;
  /** title이 문자열이 아닐 때 사용할 aria-label. */
  ariaLabel?: string;
  /**
   * 오버레이 z-index Tailwind 클래스. 기본 최상위 모달 `z-[70]`.
   * 중첩 서브 모달은 `z-[80]`. `z-50` 이하 금지(Layout 헤더 z-[60]/배너 z-[55] 위).
   */
  overlayZClassName?: string;
  /**
   * 오버레이 배경 Tailwind 클래스. 기본 정본 `bg-black/40`.
   *
   * **다른 모달 위에 겹쳐 뜨는 sub 모달에서만** 낮춰 쓴다 — 두 오버레이가 겹치면 알파가 곱해져
   * (0.4 위 0.4 = 0.64) 단독 모달보다 어두워지기 때문이다. 클래스로 받는 이유는 호출부가
   * `overlayClassName`으로 덧칠하면 정본 `bg-black/40`과 같은 우선순위로 부딪혀 어느 쪽이 이길지
   * Tailwind 출력 순서에 달리기 때문 — 배경은 base가 **하나만** 붙인다.
   */
  overlayBgClassName?: string;
  /** 열림 시 자동 포커스할 요소. 미지정 시 dialog 컨테이너에 포커스. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** 오버레이 추가 클래스(정렬 커스터마이즈 등). */
  overlayClassName?: string;
  /** dialog 패널 추가 클래스. */
  panelClassName?: string;
  /**
   * 미저장 변경 닫기 보호(기본 false). true면 닫기 요청(Esc·오버레이·✕·[[useModalRequestClose]])이
   * 즉시 닫지 않고 확인 모달(danger — Enter가 "계속 편집"인 취소 자동 포커스 규약)을 거친 뒤에만
   * `onClose`를 부른다. dirty 계산은 소비자 몫 — web은 `useDirtySnapshot` 훅으로 스냅샷 비교.
   * 확인 모달은 부모 오버레이 한 층 위(z 70→80→90→100)에 뜬다.
   */
  isDirty?: boolean;
  /**
   * 닫기 요청 전면 차단(기본 false) — 제출 진행 중 등. true면 dirty 확인조차 띄우지 않고
   * requestClose가 무시된다(web 어댑터의 `preventClose`가 여기에 매핑). Esc·오버레이·✕는
   * closeOnEsc/closeOnOverlay/showCloseButton으로도 각각 끌 수 있지만, footer 취소가 쓰는
   * requestClose까지 한 번에 잠그는 축은 이것뿐이다.
   */
  closeLocked?: boolean;
  /**
   * 본문 스크롤(**기본 true** — 2026-10-27 flip, 아래 경위). 패널 높이를 뷰포트로 제한하고
   * **본문만** 스크롤한다 — 헤더(제목·✕)와 푸터(액션 버튼)는 고정이다.
   *
   * ⛔ **스크롤을 패널에 걸지 말 것.** 패널에 걸면 헤더·푸터가 함께 스크롤돼 긴 본문에서
   * ✕와 액션 버튼이 뷰포트 밖으로 밀린다 — 오버레이 클릭 닫기를 끈 모달(공지 팝업 등)에서는
   * 닫기 수단이 **전부 그 스크롤 영역 안**이라 스크롤 위치에 따라 화면 밖에 있다.
   * ⚠️ 결정 기록 ⓘ는 이것을 「닫을 길이 사라진다」로 적었는데 **그대로 인용하지 말 것** — 패널이
   * 스크롤되므로 **도달 자체는 가능하다**(스크롤해서 찾아야 한다). 없는 것은 「길」이 아니라
   * **항상 보이는 탈출구**다. 그 차이가 심각도를 한 칸 바꾼다.
   * 선례와 경위: `apps/admin`의 `ReleaseNoteAudienceModal`·`Announcements`가
   * 그 이유를 적고 본문에 손으로 걸어 두었고, 결정은
   * `docs/decisions/2026-10-26-release-note-popup-is-generated-by-the-notes-not-referenced-by-the-popup.md` ⓘ.
   *
   * ⚠️ **기본값은 2026-10-27에 false → true로 뒤집혔다 — 「원래 그래야 했다」가 아니라
   * 「전제가 바뀌었다」다.** false였던 이유는 둘이었고 **둘 다 그 라운드가 처분했다**:
   * ① `panelClassName`으로 **패널 스크롤을 이미 든 호출부가 여럿**이었다(`max-h-[85vh]`·`[80vh]`).
   *    켜진 채로 나가면 이 옵션이 붙이는 `max-h-full`이 그 손값과 **같은 property로** 부딪혀
   *    어느 쪽이 이길지 Tailwind 출력 순서에 달린다 — 위 `overlayBgClassName`이 존재하는 이유와
   *    **같은 축**이다(base는 호출부가 쓰는 property를 덧붙이지 않는다).
   * ② `overflow-y-auto`는 **절대배치 자식을 새로 클리핑**한다 — 본문에서 밖으로 삐져나오던
   *    제안 목록·툴팁이 잘린다. 그 변화는 tsc·spec·jsdom 어느 것도 못 본다.
   * ⇒ 그 라운드가 해당 호출부에 **`bodyScroll={false}`를 명시하고 그 자리에 「왜 예외인가」를
   * 적어** 둘을 닫았다. 즉 이 두 문장은 **낡은 것이 아니라 여전히 참이고**, 다만 그 위험을 지금은
   * **예외 선언이 떠안고 있다** — 그 선언을 지우면 위험이 그대로 돌아온다.
   *
   * ⛔ **그래서 새 호출부는 아무것도 안 적으면 본문 스크롤을 «받는다».** 패널 스크롤이 필요한
   * 자리(= 푸터가 없고 오버레이 닫기가 살아 있어 패널 스크롤이 **옳은** 자리)는 `bodyScroll={false}`를
   * **명시하고 이유를 그 줄에 적어라** — 적지 않으면 다음 사람이 그것을 「깜빡한 것」과 구별할 수 없다.
   *
   * ⛔ **그 자리들은 같은 값이 아니다 — 「일괄 치환」으로 다루지 말 것.** 두 축으로 갈린다:
   * **푸터를 base `footer`로 넘기나** · **오버레이 클릭 닫기가 살아 있나.**
   * - 푸터가 없고 오버레이가 살아 있는 자리는 패널 스크롤이 **옳다** — 헤더만 함께 스크롤되고
   *   닫는 길(오버레이·Esc)이 그 스크롤 영역 **밖**에 있다. 그쪽을 치환하면 **멀쩡한 자리를
   *   고치는 것**이다(그 판정은 `ReleaseNoteAudienceModal`의 주석이 먼저 적어 뒀다).
   * - 반대로 **오버레이 닫기가 상시 off인 자리**에서는 치환이 개선이 아니라 **결함 수정**이다:
   *   닫기 수단이 전부 패널 안(✕ · 푸터 취소)이고 — 터치에는 Esc가 없으니 그 둘뿐이다 —
   *   **오버레이를 되살리는 길도 막혀 있다.** 그 자리는 이 base의 dirty 확인을 쓸 수 없어
   *   (그 z 세계가 `DIRTY_CONFIRM_Z_ABOVE` 밖이라 확인 모달이 아래로 숨는다) **오버레이 차단
   *   자체가 미저장 변경 보호**를 맡고 있다. ⇒ 그 자리를 **가장 먼저** 치환한다.
   *   ⚠️ 단 이것은 **구조 판정**이다 — 「그 본문이 오늘 실제로 넘치나」·「그 상태를 겪은 제보가
   *   있나」는 **미측정**이니 그 둘을 재기 전에 긴급도로 올리지 말 것.
   * 자리별 분류의 정본은 줄기 manifest `docs/work-streams/active/shared-modal-ux-base.md`의
   * 분류표다 — **여기에 자리를 열거하거나 개수를 적지 말 것**(그 표가 움직이면 이쪽이 조용히
   * 낡고, 낡은 수는 고칠 기계가 없다).
   */
  bodyScroll?: boolean;
  /**
   * non-modal 모드(기본 false). true면 "차단 레이어"가 아닌 "떠 있는 편집 표면"으로 동작:
   * - 오버레이 배경(bg-black/40) 미적용 + 오버레이가 클릭을 통과(pointer-events:none, 다이얼로그만 auto)
   *   → 뒤 패널(트리·속성창 등)을 모달을 띄운 채 병행 조작 가능
   * - 오버레이 클릭 닫기 무시(closeOnOverlay 무관)
   * - 포커스 트랩·body 스크롤 잠금·opener 포커스 복원 등 modal 전용 부수효과 스킵
   * - closeOnEsc(Esc)·헤더 ✕는 유지(명시적 닫기 수단)
   * 다이얼로그 셸(크기·라운드·그림자·헤더/푸터)은 modal과 완전 동일(저작 WYSIWYG 전제).
   */
  nonModal?: boolean;
}

/**
 * 공용 base 모달 — 모든 모달의 오버레이/포커스/키 UX 단일 소스.
 *
 * 보장하는 공통 UX:
 * - 오버레이 클릭·Esc·✕ = 닫기 요청(requestClose) → onClose (각각 옵션으로 비활성 가능).
 *   `isDirty`면 확인 모달을 거친 뒤에만 onClose — footer의 취소 버튼도 같은 경로로 수렴하도록
 *   [[useModalRequestClose]]를 쓴다(직접 onClose를 부르면 dirty 확인을 우회한다)
 * - 포커스 트랩(Tab/Shift+Tab이 dialog 내부 순환) + 열림 시 자동 포커스 + 닫힘 시 opener 복원
 * - body 스크롤 잠금(열림 동안)
 * - `role="dialog"` + `aria-modal` + aria-label
 * - z-index 정책([[feedback-modal-zindex-policy]]): 기본 z-[70], 서브 z-[80]
 * - `createPortal`로 body에 렌더 → overflow/transform 조상에 의한 클리핑·z 경쟁 회피
 *
 * - 본문 스크롤([[ModalProps.bodyScroll]]) — 헤더·푸터를 고정하고 본문만 스크롤한다.
 *   **2026-10-27부터 기본값이 true**다(그 전엔 옵트인이었고, 뒤집은 경위와 「예외를 어떻게 선언하나」는
 *   그 prop의 docblock에 있다 — 패널 스크롤이 옳은 자리는 `bodyScroll={false}`를 **이유와 함께** 명시한다)
 *
 * Enter/Space 기본 버튼 동작은 이 base가 강제하지 않는다(폼 모달의 입력 Enter를 침해하지 않기 위함).
 * 확인/취소 default 액션 규약은 `ConfirmModal`(Phase 2)이 담당한다.
 */
/**
 * 열린 모달의 전역 레지스트리 — Esc는 **최상단 모달만** 처리한다.
 *
 * 각 Modal이 document keydown을 각자 등록하므로, 중첩(부모 상세 → 자식 편집)에서 Esc가
 * 모든 모달에 동시에 전달되면 부모가 자신을 닫으며 dirty인 자식까지 통째로 unmount시켜
 * 닫기 확인이 무력화된다(2026-08-16 현장 담당자 인적정보 수정 실측 사고). "최상단" 판정은
 * 등록 순서가 아니라 **DOM 문서 순서**로 한다 — 같은 커밋에 중첩 마운트되면 자식 effect가
 * 부모보다 먼저 돌아 등록 순서가 시각적 스택과 어긋나지만, body 포탈은 부모→자식 순으로
 * 붙어 나중 요소가 항상 위다. dirty 확인(ConfirmModal도 base Modal)도 같은 규칙으로
 * 자연히 "가장 위 하나만 닫힘"이 된다.
 */
type OpenModalRecord = { el: HTMLElement | null };
const openModalRegistry = new Set<OpenModalRecord>();

/**
 * 오버레이 층 숫자 — modalLayers 규약(`z-[70]`→`z-[80]`→…)의 클래스 문자열에서 직접 읽는다.
 * computed z-index가 아닌 이유: jsdom(spec)엔 Tailwind CSS가 없어 항상 빈 값이고, 런타임도
 * 층은 이 클래스로만 주입된다. 규약 밖 클래스는 0 → DOM 순서 tie-break로 넘어간다.
 */
function overlayLayer(el: HTMLElement): number {
  const match = /z-\[(\d+)\]/.exec(el.className);
  return match ? Number(match[1]) : 0;
}

function topmostOpenModal(): OpenModalRecord | null {
  let top: OpenModalRecord | null = null;
  for (const record of openModalRegistry) {
    const el = record.el;
    if (!el || !el.isConnected) continue;
    if (!top || !top.el) {
      top = record;
      continue;
    }
    const layerDiff = overlayLayer(el) - overlayLayer(top.el);
    const followsInDom =
      (top.el.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
    if (layerDiff > 0 || (layerDiff === 0 && followsInDom)) top = record;
  }
  return top;
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  closeOnOverlay = true,
  closeOnEsc = true,
  showCloseButton = true,
  ariaLabel,
  overlayZClassName = 'z-[70]',
  overlayBgClassName = 'bg-black/40',
  initialFocusRef,
  overlayClassName = '',
  panelClassName = '',
  isDirty = false,
  closeLocked = false,
  bodyScroll = true,
  nonModal = false,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const registryRecordRef = useRef<OpenModalRecord | null>(null);

  // 열림 동안 전역 레지스트리에 등록 — nonModal은 배경 병행 조작용이라 끼지 않는다
  // (등록하면 뒤 모달의 Esc를 가로챈다). el은 층(z-[N]) 클래스를 가진 오버레이 요소.
  useEffect(() => {
    if (!open || nonModal) return;
    const record: OpenModalRecord = { el: overlayRef.current };
    registryRecordRef.current = record;
    openModalRegistry.add(record);
    return () => {
      openModalRegistry.delete(record);
      registryRecordRef.current = null;
    };
  }, [open, nonModal]);

  // dirty 닫기 확인 노출 여부 — 닫기 요청이 들어왔는데 isDirty였을 때만 켠다.
  const [showDirtyConfirm, setShowDirtyConfirm] = useState(false);

  /**
   * 모든 닫기 경로의 단일 수렴점 — Esc·오버레이·✕·footer 취소([[useModalRequestClose]])가
   * 전부 여기로 온다. dirty면 확인을 거치고, 아니면 그대로 onClose.
   */
  const requestClose = useCallback(() => {
    if (closeLocked) return;
    if (isDirty) {
      setShowDirtyConfirm(true);
      return;
    }
    onClose();
  }, [closeLocked, isDirty, onClose]);

  // Esc 핸들러가 항상 최신 requestClose를 보게 ref로 우회(effect 재등록 최소화).
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;

  // 닫혔다 다시 열릴 때 이전의 확인 잔상이 남지 않게 정리.
  useEffect(() => {
    if (!open) setShowDirtyConfirm(false);
  }, [open]);

  // Esc = 닫기 요청 (document 레벨 keydown — 포커스가 어디에 있든 동작).
  // 레지스트리 최상단(DOM 순서 기준)일 때만 응답한다 — 중첩 모달·dirty 확인(ConfirmModal도
  // base Modal이라 합류)에서 "가장 위 하나만" 닫힌다. 최상단이 closeOnEsc=false면 Esc는
  // 아무것도 안 한다(아래 모달로 흘려보내지 않음 — 위가 거부했는데 뒤가 닫히면 더 위험하다).
  useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // nonModal은 레지스트리 밖(배경 병행 조작용) — 진짜 모달이 하나라도 떠 있으면 그쪽이 우선.
      if (nonModal) {
        if (topmostOpenModal() !== null) return;
      } else if (topmostOpenModal() !== registryRecordRef.current) {
        return;
      }
      e.preventDefault();
      requestCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeOnEsc, nonModal]);

  // 열림 시 opener 저장 + 자동 포커스, 닫힘 시 opener로 포커스 복원.
  // non-modal은 뒤 패널 병행 조작이 목적이라 포커스를 다이얼로그로 뺏지 않는다(스킵).
  useEffect(() => {
    if (!open || nonModal) return;
    openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
    // 렌더 후 포커스(포탈 DOM 부착 보장 위해 microtask 뒤).
    const focusTarget = initialFocusRef?.current ?? dialogRef.current;
    focusTarget?.focus();
    const opener = openerRef.current;
    return () => {
      // 언마운트/닫힘 시 원래 포커스 복원(요소가 아직 문서에 있으면).
      opener?.focus?.();
    };
    // initialFocusRef/onClose는 의도적으로 deps 제외(열림 순간의 ref만 사용).
    // (packages 스코프는 react-hooks/exhaustive-deps 미적용)
    // nonModal은 본문에서 읽으므로 deps에 포함 — 열린 채 nonModal이 토글되면 다시 평가돼야 한다.
  }, [open, nonModal]);

  // body 스크롤 잠금. non-modal은 배경을 잠그지 않는다(병행 조작 허용).
  // nonModal을 deps에 포함해 열린 채 토글 시 잠금/해제가 반영되게 한다.
  useEffect(() => {
    if (!open || nonModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, nonModal]);

  // 포커스 트랩 — Tab/Shift+Tab이 dialog 내부에서 순환. non-modal은 트랩하지 않음.
  const handleTrapKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (nonModal) return;
    if (e.key !== 'Tab') return;
    const dialog = dialogRef.current;
    if (!dialog) return;
    const focusables = Array.from(
      dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    );
    if (focusables.length === 0) {
      // 포커스 가능한 자식이 없으면 dialog 밖으로 새지 않게 차단.
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || active === dialog) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const labelText = typeof title === 'string' ? title : ariaLabel;

  // non-modal: 배경(bg-black/40) 제거 + 오버레이가 클릭을 통과(pointer-events:none)해 뒤
  // 패널을 병행 조작. 다이얼로그 패널만 pointer-events:auto로 클릭 수신. 오버레이 클릭 닫기 무시.
  const overlayBgClass = nonModal ? '' : overlayBgClassName;
  const overlayStyle = nonModal ? { pointerEvents: 'none' as const } : undefined;
  const panelStyle = nonModal ? { pointerEvents: 'auto' as const } : undefined;

  const dirtyConfirmZ = DIRTY_CONFIRM_Z_ABOVE[overlayZClassName] ?? 'z-[100]';

  // 본문 스크롤은 세 자리가 **함께** 바뀌어야 성립한다(패널이 높이를 제한 → 헤더·푸터가 고정 →
  // 본문만 남은 높이에서 스크롤). 꺼져 있으면 세 자리 모두 빈 문자열이라 종전 렌더와 동일하다.
  const panelScrollClass = bodyScroll ? BODY_SCROLL_PANEL_CLASS : '';
  const fixedBlockClass = bodyScroll ? BODY_SCROLL_FIXED_CLASS : '';
  const bodyScrollClass = bodyScroll ? BODY_SCROLL_BODY_CLASS : '';

  return createPortal(
    <ModalRequestCloseContext.Provider value={requestClose}>
    <div
      ref={overlayRef}
      className={`fixed inset-0 ${overlayZClassName} flex items-center justify-center ${overlayBgClass} p-4 ${overlayClassName}`}
      style={overlayStyle}
      onClick={!nonModal && closeOnOverlay ? requestClose : undefined}
    >
      {/*
        라운드·그림자는 목업 `.modal-dialog` 정본(20px + 깊은 그림자). 카드(8~18px)보다 크게
        잡힌 건 오버레이 위에 떠 있는 층이라서다 — 카드 토큰으로 낮추지 말 것.
      */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={nonModal ? undefined : 'true'}
        aria-label={labelText}
        tabIndex={-1}
        // panelClassName은 **맨 뒤**에 둔다 — 호출부가 셸 시각을 덧칠하는 자리다. 다만 같은
        // property를 쓰면 승자가 Tailwind 출력 순서에 달리므로, bodyScroll이 꺼져 있을 때는
        // panelScrollClass가 빈 문자열이라 `max-height`를 아예 내보내지 않는다.
        className={`w-full ${SIZE_CLASS[size]} rounded-modal bg-white shadow-modal outline-none ${panelScrollClass} ${panelClassName}`}
        style={panelStyle}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleTrapKeyDown}
      >
        {(title || showCloseButton) && (
          <div
            className={`flex items-center justify-between border-b border-line px-5 py-3 ${fixedBlockClass}`}
          >
            {title ? (
              <h2 className="text-base font-bold text-ink-800">{title}</h2>
            ) : (
              <span />
            )}
            {showCloseButton && (
              <button
                type="button"
                onClick={requestClose}
                // Esc를 끈 창(closeOnEsc=false)에 「(Esc)」를 붙이면 안내가 거짓이 된다
                title={closeOnEsc ? '닫기 (Esc)' : '닫기'}
                aria-label="닫기"
                // 목업 `.modal-close`는 34px이지만 **32px(h-8)로 스냅**한다 — 아이콘 액션을
                // 8px 그리드·터치 타깃 기준 32px로 통일한 Button 결정과 같은 축이고,
                // 임의값(`h-[34px]`)은 packages/ui에서 조용히 무스타일이 된다(위 CloseGlyph 주석).
                className="grid h-8 w-8 flex-none place-items-center rounded-control text-ink-400 transition-colors hover:bg-surface-soft hover:text-ink-900"
              >
                <CloseGlyph />
              </button>
            )}
          </div>
        )}
        <div className={`px-5 py-4 ${bodyScrollClass}`}>{children}</div>
        {footer && (
          <div
            className={`flex justify-end gap-2 border-t border-line px-5 py-3 ${fixedBlockClass}`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
    {/* dirty 닫기 확인 — danger 규약(취소="계속 편집" 자동 포커스)이라 Enter 오조작으로
        변경사항이 날아가지 않는다. 확인의 안쪽 Modal은 isDirty가 없어 재귀하지 않는다. */}
    {showDirtyConfirm && (
      <ConfirmModal
        open
        title="닫기 확인"
        body="저장하지 않은 변경사항이 있습니다. 닫으면 변경사항이 사라집니다."
        confirmLabel="닫기"
        cancelLabel="계속 편집"
        danger
        overlayZClassName={dirtyConfirmZ}
        overlayBgClassName={DIRTY_CONFIRM_OVERLAY_BG}
        onConfirm={() => {
          setShowDirtyConfirm(false);
          onClose();
        }}
        onClose={() => setShowDirtyConfirm(false)}
      />
    )}
    </ModalRequestCloseContext.Provider>,
    document.body,
  );
}
